import { Link } from "react-router-dom";
import styles from "./HeaderProperty.module.css";
import Breadcrumb from "../../UI/Breadcrumb";
import Button from "../../UI/Button";
import Text from "../../UI/Text";
import HeaderPropertyForm from "../AddFormComponents/HeaderPropertyForm";
import {
  PROPERTY_FOLDER_INITIAL_VALUES,
  PROPERTY_INITIAL_VALUES,
  propertyHeadSchema,
  PropertyStatus,
} from "../../../models/property.js";
// @ts-expect-error pure js
import PartForm from "../../UI/PartForm";
// @ts-expect-error pure js
import { useModal } from "../../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { toInitialValuesFromSchema } from "../../../models/utils";
// @ts-expect-error pure js
import { propertyFolderHeadSchema } from "../../../models/propertyFolder";
// @ts-expect-error pure js
import PropertyFolderAddProperty from "../PropertyFolderAddProperty";
// @ts-expect-error pure js
import useProfileBailleurs from "../../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import usePropertyFolders from "../../../hooks/use-property-folders";
import PhotoGallery from "../PhotoGallery";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import usePropertyPhotos from "../../../hooks/use-property-photos";
import Title from "../../UI/Title.tsx";
import Avatar from "../../UI/Avatar.tsx";
import Tooltip from "../../UI/Tooltip.tsx";
import Status from "../../UI/Status.tsx";
import CopyToClipboard from "../../UI/CopyToClipboard.tsx";
import type { Property } from "../../../models/property";
import type { PropertyFolder } from "../../../models/propertyFolder";
import type { BreadcrumbItem } from "../../UI/Breadcrumb";
import VideoTutorialsButton from "../../VideoTutorials/VideoTutorialsButton";
import useActiveTab from "../../../hooks/use-active-tab";

interface HeaderPropertyProps {
  property: Property;
  onDelete: (id: number) => void;
  onArchive: (id: number) => void;
  onReactivate: (id: number) => void;
  onUpdate: (data: Record<string, unknown>) => Promise<void>;
  isFolder?: boolean;
  onMoveToFolder?: (propertyId: number) => Promise<void>;
  onMoveFolderToFolder?: (propertyFolderId: number) => Promise<void>;
}

export default function HeaderProperty({
  property,
  onDelete,
  onArchive,
  onReactivate,
  onUpdate,
  isFolder,
  onMoveToFolder,
  onMoveFolderToFolder,
}: HeaderPropertyProps) {
  const [
    HeaderPropertyFormModal,
    closeHeaderPropertyFormModal,
    openHeaderPropertyFormModal,
  ] = useModal();
  const [MoveFolder, closeMoveFolder, openMoveFolder] = useModal();
  const { profileBailleursById } = useProfileBailleurs({
    withById: true,
  });
  const { propertyFoldersById } = usePropertyFolders({ withById: true }) as {
    propertyFoldersById: Record<number, PropertyFolder>;
  };

  const profileBailleur = profileBailleursById[property.id_profile_nom_owner];

  const folderChain: PropertyFolder[] = [];
  let parentId: number | undefined = isFolder
    ? (property as unknown as PropertyFolder).id_parent
    : property.id_property_folder;
  while (parentId && propertyFoldersById[parentId]) {
    const folder = propertyFoldersById[parentId];
    folderChain.unshift(folder);
    parentId = folder.id_parent;
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "Tableau de bord", to: "/bailleur" },
    { label: "Biens", to: "/bailleur/properties" },
    ...folderChain.map((folder) => ({
      label: folder.nom,
      to: `/bailleur/properties/folder/${folder.id}`,
    })),
    { label: property.nom },
  ];

  const { photos, createPhotos } = usePropertyPhotos(property.id, isFolder);
  const activeTab = useActiveTab("prop-show", "vue-ensemble");
  const showTutorials = !isFolder && activeTab === "vue-ensemble";

  async function handleSubmit(data: Record<string, unknown>) {
    const { _pendingPhotos, ...rest } = data;
    const pendingPhotos = _pendingPhotos as
      | Array<{ src: string; mimetype?: string }>
      | undefined;

    if (pendingPhotos && pendingPhotos.length > 0) {
      await createPhotos(pendingPhotos);
    }

    await onUpdate(rest);
  }

  const formatAddress = () => {
    const parts = [];
    if (property.rue) parts.push(property.rue);
    if (property.complement_adresse) parts.push(property.complement_adresse);
    if (property.cp || property.ville) {
      parts.push(`${property.cp || ""} ${property.ville || ""}`.trim());
    }
    return parts.join(", ");
  };

  return (
    <>
      {isFolder && onMoveToFolder && onMoveFolderToFolder && (
        <MoveFolder>
          <PropertyFolderAddProperty
            propertyFolderId={property.id}
            onMoveToFolder={async (propertyId: number) => {
              await onMoveToFolder(propertyId);
              closeMoveFolder();
            }}
            onMoveFolderToFolder={async (propertyFolderId: number) => {
              await onMoveFolderToFolder(propertyFolderId);
              closeMoveFolder();
            }}
          />
        </MoveFolder>
      )}
      <HeaderPropertyFormModal>
        <PartForm
          partForm={
            <HeaderPropertyForm isFolder={isFolder} propertyId={property.id} />
          }
          validationSchema={
            isFolder ? propertyFolderHeadSchema : propertyHeadSchema
          }
          initialValues={{
            ...toInitialValuesFromSchema(
              isFolder ? propertyFolderHeadSchema : propertyHeadSchema,
              property,
              isFolder
                ? PROPERTY_FOLDER_INITIAL_VALUES
                : PROPERTY_INITIAL_VALUES,
            ),
            _pendingPhotos: [],
          }}
          submitHandler={handleSubmit}
          deleteHandler={
            property.status === PropertyStatus.Archive.status
              ? () => onDelete(property.id)
              : undefined
          }
          archiveHandler={
            property.status === PropertyStatus.Actif.status
              ? () => onArchive(property.id)
              : undefined
          }
          reactivateHandler={
            property.status === PropertyStatus.Archive.status
              ? () => onReactivate(property.id)
              : undefined
          }
          closeModal={closeHeaderPropertyFormModal}
        />
      </HeaderPropertyFormModal>

      <div className="content">
        <div className={styles.header}>
          <div className={styles.topRow}>
            <Breadcrumb items={breadcrumbItems} />
            <div className={styles.buttons}>
              <Button
                variant="secondary"
                onClick={openHeaderPropertyFormModal}
                icon={IconPencil}
                requiresWrite
              >
                <span className={styles.mobileHidden}>Modifier</span>
              </Button>
              {isFolder && property.status === PropertyStatus.Actif.status && (
                <Button onClick={openMoveFolder} icon={IconPlus} requiresWrite>
                  Ajouter un bien
                </Button>
              )}
            </div>
          </div>

          <PhotoGallery
            photos={photos}
            onPlaceholderClick={openHeaderPropertyFormModal}
            propertyType={isFolder ? "folder" : property.type}
          />

          <div className={styles.titleRow}>
            <div>
              <Title variant="hecto" weight="bold">
                {property.nom}
              </Title>
              <Text className={styles.address}>{formatAddress()}</Text>
            </div>
            <div className={styles.titleActions}>
              {!isFolder && (
                <div className={styles.propertyNumber}>
                  <Tooltip
                    content="Numéro à communiquer à vos candidats : ils le saisissent dans leur espace Qalimo pour postuler à ce bien."
                    direction="bottom"
                  >
                    <Text as="span">N° du bien</Text>
                  </Tooltip>
                  <Status
                    as="span"
                    color="primary"
                    variant="base"
                    weight="bold"
                    text={property.id}
                  />
                  <CopyToClipboard
                    toCopy={String(property.id)}
                    variant="plain"
                  />
                </div>
              )}
              {showTutorials && <VideoTutorialsButton page="bien" />}
            </div>
          </div>

          <div className={styles.profileSection}>
            <Avatar
              initials={profileBailleur?.nom_profile.charAt(0).toUpperCase()}
              className={styles.profileAvatar}
            />
            <div>
              <Text as="span" weight="bold">
                Bailleur :{" "}
              </Text>
              <Text as="span" weight="medium">
                <Link
                  to={`/bailleur/profiles/${property.id_profile_nom_owner}`}
                >
                  {profileBailleur?.nom_profile}
                </Link>
              </Text>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
